import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/lib/utils';
import { RecordLink } from './RecordLink';
import type { RecordType } from './registry';

interface RecordSource {
  type: string | null;
  id: string;
  label: string;
}

export interface JournalEntryDetail {
  id: string;
  entryNumber: string;
  date: string;
  description: string;
  status: string;
  notes?: string | null;
  referenceType?: string | null;
  postedAt?: string | null;
  voidedAt?: string | null;
  createdAt?: string;
  voidReason?: string | null;
  createdByName?: string | null;
  postedByName?: string | null;
  voidedByName?: string | null;
  source?: RecordSource | null;
  reversalOf?: RecordSource | null;
  reversedBy?: RecordSource | null;
  /** POS-I244: the Reversal entry of a reversed stock adjustment is not voidable. */
  voidBlocked?: boolean;
  lines: Array<{
    id: string;
    debitAmount: number | string;
    creditAmount: number | string;
    description?: string | null;
    account?: { code: string; name: string } | null;
  }>;
}

const amount = (v: number | string) => (Number(v) ? formatCurrency(Number(v)) : '');
const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '');

function Who({ label, name, at }: { label: string; name?: string | null; at?: string | null }) {
  if (!name && !at) return null;
  return (
    <div className="flex flex-wrap justify-between gap-x-3 gap-y-0.5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-end">
        {name}
        {at && <span className="ms-2 text-xs text-muted-foreground" dir="ltr">{when(at)}</span>}
      </dd>
    </div>
  );
}

/** Read-only journal entry detail for the record viewer (POS-I238). No Post/Void here. */
export function JournalEntryDetailBody({ data }: { data: JournalEntryDetail }) {
  const { t } = useTranslation('records');
  const totalDebit = data.lines.reduce((s, l) => s + Number(l.debitAmount || 0), 0);
  const totalCredit = data.lines.reduce((s, l) => s + Number(l.creditAmount || 0), 0);

  const linkOrText = (s: RecordSource) =>
    s.type ? (
      <RecordLink type={s.type as RecordType} id={s.id} label={s.label} />
    ) : (
      <span>{s.label}</span>
    );

  return (
    <div className="space-y-5 text-sm">
      <dl className="space-y-1.5">
        <div className="flex flex-wrap justify-between gap-x-3">
          <dt className="text-muted-foreground">{t('jeDate')}</dt>
          <dd dir="ltr">{new Date(data.date).toLocaleDateString()}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{t('jeDescription')}</dt>
          <dd className="mt-0.5 break-words">{data.description}</dd>
        </div>
      </dl>

      {data.lines.length === 0 ? (
        <p className="text-muted-foreground">{t('jeNoLines')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="py-1 text-start font-medium">{t('jeAccount')}</th>
                <th className="py-1 text-end font-medium">{t('jeDebit')}</th>
                <th className="py-1 text-end font-medium">{t('jeCredit')}</th>
              </tr>
            </thead>
            <tbody>
              {data.lines.map((l) => (
                <tr key={l.id} className="border-b align-top last:border-0">
                  <td className="py-1.5">
                    <span className="me-2 font-mono text-xs text-muted-foreground" dir="ltr">{l.account?.code}</span>
                    {l.account?.name}
                    {l.description && <p className="text-xs text-muted-foreground">{l.description}</p>}
                  </td>
                  <td className="py-1.5 text-end tabular-nums" dir="ltr">{amount(l.debitAmount)}</td>
                  <td className="py-1.5 text-end tabular-nums" dir="ltr">{amount(l.creditAmount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t font-semibold" data-testid="record-viewer-totals">
                <td className="py-1.5">{t('jeTotal')}</td>
                <td className="py-1.5 text-end tabular-nums" dir="ltr">{formatCurrency(totalDebit)}</td>
                <td className="py-1.5 text-end tabular-nums" dir="ltr">{formatCurrency(totalCredit)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <dl className="space-y-1.5">
        <Who label={t('jeEnteredBy')} name={data.createdByName} at={data.createdAt} />
        <Who label={t('jePostedBy')} name={data.postedByName} at={data.postedAt} />
        <Who label={t('jeVoidedBy')} name={data.voidedByName} at={data.voidedAt} />
        {data.voidReason && (
          <div>
            <dt className="text-muted-foreground">{t('jeVoidReason')}</dt>
            <dd className="mt-0.5 break-words">{data.voidReason}</dd>
          </div>
        )}
        {data.reversedBy && (
          <div className="flex flex-wrap justify-between gap-x-3">
            <dt className="text-muted-foreground">{t('jeReversedBy')}</dt>
            <dd>{linkOrText(data.reversedBy)}</dd>
          </div>
        )}
        {data.reversalOf && (
          <div className="flex flex-wrap justify-between gap-x-3">
            <dt className="text-muted-foreground">{t('jeReverses')}</dt>
            <dd>{linkOrText(data.reversalOf)}</dd>
          </div>
        )}
      </dl>

      {data.status === 'POSTED' && data.voidBlocked && (
        <p className="text-xs text-muted-foreground" data-testid="record-viewer-void-blocked">{t('jeVoidBlocked')}</p>
      )}

      <div className="border-t pt-3" data-testid="record-viewer-source">
        <p className="mb-1 text-xs font-medium text-muted-foreground">{t('source')}</p>
        {data.source ? linkOrText(data.source) : <span>{t('manualEntry')}</span>}
      </div>
    </div>
  );
}
